import { RefObject } from "react";
import { PayloadAction } from "@reduxjs/toolkit";
import { all, call, Effect, put, takeEvery, takeLatest, select } from "redux-saga/effects";
import { paymentApi } from "../../api/PaymentApi";
import { refundApi } from "../../api/RefundApi";
import { paymentSlice, PaymentState } from "../reducer/payment";
import { stateSlice } from "../reducer/state";
import { TaxCalculationResponse } from "../../model/tax_calculations";
import { Refund, refundToRequest } from "../../model/final_balance/refund.d";
import { signatureApi } from "../../api/SignatureApi";
import { RootState } from "..";
import { push } from "redux-first-history";
import SignatureCanvas from "react-signature-canvas";
import { ApiResponse } from "../../model/api";
import { PaymentIntentToken } from "../../model/final_balance/payment_intent_token";
import { PaymentIntentRequest } from "../../model/final_balance/payment_intent_request";
import { PayLaterRequest } from "../../model/final_balance/pay_later";
import { ApiError, ApiErrorInterface, SerializedApiError } from "../../model/errors/errors";
import { PaymentOption } from "../../model/final_balance/payment_option.d";

function* confirmRefundSaga(): Generator<Effect, void, any> {
  const { refund } = (yield select((state: RootState) => state.payment)) as PaymentState;

  try {
    yield call(refundApi.create, refundToRequest(refund));
    yield put(paymentSlice.actions.updateRefundSuccess());
    yield put(push('/payment/complete'));
  } catch (error: SerializedApiError<undefined> | any) {
    yield put(paymentSlice.actions.updateRefundError(ApiError.deserialize(error)?.toString() ?? String(error)));
  }
}

function* confirmSignatureSaga({
  payload: {signatureRef, acceptReturn}
}: PayloadAction<{ signatureRef: RefObject<SignatureCanvas | null>, acceptReturn: boolean}>): Generator<Effect, void, any> {
  if (typeof signatureRef === 'undefined' || typeof signatureRef.current === 'undefined' || signatureRef?.current?.isEmpty()) {
    yield put(paymentSlice.actions.signatureRequestError("Please sign your tax return to file"));
  } else if (!acceptReturn) {
    yield put(paymentSlice.actions.signatureRequestError("Please check the acceptance box to file your return"));
  } else {
    try {
      // @ts-ignore Above logic already rules out the possibility of this being null
      yield put(paymentSlice.actions.signatureRequest(signatureRef.current.toDataURL()));
    } catch (error: SerializedApiError<undefined> | any) {
      yield put(push('balance/sorry', { state: { error: error } }))
    }
  }
  
}

function* fetchPaymentIntentSaga({
  payload: {taxReturnID, includesEstimatedPayment},
}: PayloadAction<{taxReturnID: number, includesEstimatedPayment: boolean}>): Generator<Effect, void, any> {
  try {
    const paymentIntentRequest: PaymentIntentRequest = {
      tax_return_id: taxReturnID,
      includes_quarterly_estimated_payment: includesEstimatedPayment
    };
    const response: ApiResponse<PaymentIntentToken> = yield call(paymentApi.getPaymentIntent, paymentIntentRequest);
    yield put(paymentSlice.actions.fetchPaymentIntentSuccess(response.data));
  } catch (error: any) {
    yield put(paymentSlice.actions.fetchPaymentIntentError(ApiError.deserialize(error)?.toString() ?? String(error)));
  }
}

function* finalBalanceSaga(): Generator<Effect, void, any> {
  try {
    const response: TaxCalculationResponse = yield call(paymentApi.finalBalance);
    yield put(paymentSlice.actions.finalBalanceSuccess(response.data));
  } catch (error: SerializedApiError<undefined> | any) {
    yield put(paymentSlice.actions.finalBalanceError(ApiError.deserialize(error)?.toString() ?? String(error)));
    yield put(push('/balance/sorry', { state: { error: error } }))
  }
}

function* updateRefundSaga({ payload }: PayloadAction<Refund>): Generator<Effect, void, any> {
  
}

function* signatureRequestSaga({ payload }: PayloadAction<string>): Generator<Effect, void, any> {
  const { finalBalance } = (yield select((state: RootState) => state.payment)) as PaymentState;

  try {
    // TODO check backend api response, etc
    yield call(signatureApi.signCertificate, payload);
    yield put(paymentSlice.actions.signatureRequestSuccess());
    yield put(stateSlice.actions.signReturn());
    yield put(push('/transition/post-signature'))
  } catch (error: SerializedApiError<undefined> | any) {
    yield put(paymentSlice.actions.signatureRequestError(ApiError.deserialize(error)?.toString() ?? String(error)));
  }
}

function* payLaterSaga({ payload }: PayloadAction<PayLaterRequest>): Generator<Effect, void, any> {
  try {
    yield call(paymentApi.payLater, payload);

    yield put(paymentSlice.actions.payLaterRequestSuccess());
    yield put(paymentSlice.actions.setPaymentChoice(PaymentOption.PayLater));
    yield put(push('/payment/complete'));
  } catch (error: SerializedApiError<undefined> | any) {
    yield put(paymentSlice.actions.payLaterRequestError(ApiError.deserialize(error)?.toString() ?? String(error)));
  }
}

export default function* paymentSaga() {
  yield all([takeLatest(paymentSlice.actions.fetchPaymentIntent, fetchPaymentIntentSaga)]);
  yield all([takeEvery(paymentSlice.actions.updateRefund, updateRefundSaga)]);
  yield all([takeEvery(paymentSlice.actions.signatureRequest, signatureRequestSaga)]);
  yield all([takeLatest(paymentSlice.actions.finalBalanceRequest, finalBalanceSaga)]);
  yield all([takeEvery(paymentSlice.actions.confirmRefund, confirmRefundSaga)]);
  yield all([takeEvery(paymentSlice.actions.confirmSignature, confirmSignatureSaga)]);
  yield all([takeEvery(paymentSlice.actions.payLaterRequest, payLaterSaga)]);
}
